import { useStore } from '@nanostores/react';
import { Root, Item } from '@radix-ui/react-radio-group';
import { memo, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { SectionRow } from '~/components/dialogs/settings/SectionContainer';
import { Dialog, DialogActions, DialogButton, DialogHeader } from '~/components/ui/Dialog';
import { AGENT_OPTIONS, getAgentName, type SupportedAgents } from '~/lib/agents';
import { userCanUseClaudeCodeAgent } from '~/lib/agents';
import { useChatAgent } from '~/lib/hooks/useChatAgent';
import { menuStore } from '~/lib/stores/menu';
import { supabaseStore } from '~/lib/stores/supabase';
import { userStore } from '~/lib/stores/user';
import { classNames } from '~/utils/classNames';

export const ProjectAgent = memo(({ canChangeAgent }: { canChangeAgent: boolean }) => {
  const { agent, setAgent } = useChatAgent();
  const user = useStore(userStore.user);
  const supabaseRequiresAction = useStore(supabaseStore.promptDisabled);

  const [nextAgent, setNextAgent] = useState<SupportedAgents | undefined>();
  const [isSwitchingAgent, setIsSwitchingAgent] = useState(false);

  const hasClaudeCodeAccess = useMemo(() => {
    return userCanUseClaudeCodeAgent(user);
  }, [user]);

  const handleCancelSwitch = useCallback(() => {
    setNextAgent(undefined);
  }, []);

  const handleConfirmSwitch = useCallback(async () => {
    if (!nextAgent) {
      return;
    }

    setIsSwitchingAgent(true);
    await setAgent(nextAgent);
    toast.success(`Agent switched to ${getAgentName(nextAgent)}`);
    setIsSwitchingAgent(false);
    setNextAgent(undefined);
    menuStore.closeDialog();
  }, [nextAgent, setAgent]);

  if (!hasClaudeCodeAccess) {
    return null;
  }

  let content = (
    <Root
      name="project-visibility"
      value={agent}
      className="flex w-full flex-col md:flex-row md:justify-start gap-4"
      onValueChange={(value) => setNextAgent(value as SupportedAgents)}
    >
      {AGENT_OPTIONS.map((item, index) => (
        <AgentItem key={`${item.value}-${index}`} item={item} />
      ))}
    </Root>
  );

  if (supabaseRequiresAction) {
    content = (
      <p className="text-bolt-elements-text-secondary w-2/3">
        You can't switch agents until you approve or discard the pending database changes.
      </p>
    );
  }

  if (!canChangeAgent) {
    content = (
      <p className="text-bolt-elements-text-secondary w-2/3">
        You do not have permission to change the agent of this project.
      </p>
    );
  }

  return (
    <>
      <SectionRow
        title={'Project Agent'}
        layout="vertical"
        innerClassName="flex flex-col gap-4"
        titleClassName="font-semibold text-sm mb-0"
        action={content}
      />

      <AgentSwitchDialog
        nextAgent={nextAgent}
        isSwitchingAgent={isSwitchingAgent}
        onCancel={handleCancelSwitch}
        onConfirm={handleConfirmSwitch}
      />
    </>
  );
});

const AgentSwitchDialog = memo(
  ({
    nextAgent,
    isSwitchingAgent,
    onCancel,
    onConfirm,
  }: {
    nextAgent: SupportedAgents | undefined;
    isSwitchingAgent: boolean;
    onCancel: () => void;
    onConfirm: () => void;
  }) => {
    return (
      <Dialog
        open={!!nextAgent}
        closeOnEscape={!isSwitchingAgent}
        closeOnBackdropClick={!isSwitchingAgent}
        showCloseButton={!isSwitchingAgent}
        onClose={onCancel}
        contentClassName="selection-accent max-w-md"
      >
        <DialogHeader
          title="Confirm agent switch"
          description={
            <div className="pt-4 grid gap-2">
              <p>
                Switching agent will <b className="text-bolt-ds-textPrimary font-semibold">clear your chat history</b>.
              </p>
              <p>This means the new agent won’t remember any previous messages or context.</p>
            </div>
          }
        />

        <DialogActions>
          <DialogButton variant="secondary" onClick={onCancel} disabled={isSwitchingAgent}>
            Cancel
          </DialogButton>

          <DialogButton loading={isSwitchingAgent} variant="tint" onClick={onConfirm}>
            Confirm switch
          </DialogButton>
        </DialogActions>
      </Dialog>
    );
  },
);

ProjectAgent.displayName = 'ProjectAgent';

const AgentItem = memo(({ item }: { item: { value: string; label: string; disabled?: boolean } }) => {
  return (
    <Item
      className={classNames(
        'group flex flex-col gap-1 p-4 border border-bolt-elements-borderColor min-w-[160px] bg-bolt-ds-surfaceOne rounded-[8px] outline-none',
        'enabled:hover:bg-bolt-elements-item-backgroundActive focus-visible:ring-2 focus-visible:ring-bolt-elements-item-backgroundActive',
        'data-[state=checked]:bg-bolt-ds-brand/10 data-[state=checked]:border-bolt-ds-brand/10 enabled:hover:data-[state=checked]:bg-bolt-ds-brand/10',
        'disabled:opacity-50',
      )}
      value={item.value}
      disabled={item.disabled}
    >
      <div className="flex gap-1 items-center font-medium group-data-[state=checked]:text-bolt-ds-brand">
        <p>{item.label}</p>
        {item.disabled && (
          <div className="ml-2 bg-bolt-ds-surfaceThree text-bolt-ds-textPrimary px-2 py-0.5 rounded-full text-[9px] uppercase font-medium tracking-wider">
            Coming soon
          </div>
        )}
      </div>
    </Item>
  );
});

AgentItem.displayName = 'AgentItem';
